import { Alert, Modal, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import * as api from "../api/services.ts"
import {
  setActiveConversation,
  setDraft,
  setMessages
} from "../redux/messaging.slice.ts"
import {
  deleteMessage,
  discardFailedMessage,
  editMessage,
  fetchMessages,
  fetchOlderMessages,
  markRead,
  persistDraft,
  retrySend,
  sendMessage,
  updateNotificationLevel
} from "../redux/messaging.thunks.ts"
import type {
  Conversation,
  Message,
  MessageAttachment,
  NotificationLevel,
  PendingMessage
} from "../types/messaging.types.ts"
import { ConversationHeader } from "./ConversationHeader.tsx"
import { MessageComposer } from "./MessageComposer.tsx"
import { MessageHistory } from "./MessageHistory.tsx"
import { SaveToProjectFilesModal } from "./SaveToProjectFilesModal.tsx"

const DRAFT_DEBOUNCE_MS = 800

interface ConversationViewProps {
  conversation: Conversation
  portal?: boolean
  showBack?: boolean
  onBack?: () => void
  /** Focus a specific message on open — a search hit or a notification link. */
  focusMessageId?: string | null
  /** True while the reader has traded the centred column for the full width. */
  expanded?: boolean
  /** Omitted where there is no wider space to expand into, as in the portal. */
  onToggleExpand?: () => void
}

export function ConversationView({
  conversation,
  portal = false,
  showBack = false,
  onBack,
  focusMessageId = null,
  expanded = false,
  onToggleExpand
}: ConversationViewProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const currentUserId = useAppSelector((state) => state.auth.userInfo?.id)
  const isConnected = useAppSelector((state) => state.messaging.isConnected)
  const messages = useAppSelector(
    (state) => state.messaging.messagesByConversation[conversation.id]
  )
  const hasMore = useAppSelector(
    (state) => state.messaging.hasMoreByConversation[conversation.id] ?? false
  )
  const loading = useAppSelector(
    (state) => state.messaging.messagesLoading[conversation.id] ?? false
  )
  const error = useAppSelector(
    (state) => state.messaging.messagesError[conversation.id] ?? false
  )
  const draft = useAppSelector(
    (state) => state.messaging.drafts[conversation.id] ?? ""
  )
  const pendingAll = useAppSelector((state) => state.messaging.pending)
  const revokedConversationId = useAppSelector(
    (state) => state.messaging.revokedConversationId
  )

  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [highlightedId, setHighlightedId] = useState<string | null>(
    focusMessageId
  )
  const [savingAttachment, setSavingAttachment] =
    useState<MessageAttachment | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const draftTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const lastMarkedRef = useRef<string>("")

  const pending = useMemo(
    () =>
      Object.values(pendingAll).filter(
        (row: PendingMessage) => row.conversation === conversation.id
      ),
    [pendingAll, conversation.id]
  )

  useEffect(() => {
    dispatch(setActiveConversation(conversation.id))
    if (!messages) {
      dispatch(fetchMessages({ conversationId: conversation.id, portal }))
    }
  }, [conversation.id])

  useEffect(() => {
    setHighlightedId(focusMessageId)
  }, [focusMessageId])

  // Drafts are debounced to the server so they survive a device change, and
  // written to the store immediately so the composer never lags the keystroke.
  const handleDraftChange = useCallback(
    (value: string) => {
      dispatch(setDraft({ conversationId: conversation.id, draft: value }))
      if (draftTimer.current) clearTimeout(draftTimer.current)
      draftTimer.current = setTimeout(() => {
        dispatch(
          persistDraft({ conversationId: conversation.id, draft: value, portal })
        )
      }, DRAFT_DEBOUNCE_MS)
    },
    [conversation.id, dispatch, portal]
  )

  const handleReachedMessage = useCallback(
    (message: Message) => {
      if (lastMarkedRef.current === message.created) return
      // Delivery is not reading. A message that lands while the tab is in the
      // background stays unread — and because this guard returns before the
      // marker moves, the effect below can pick it up when the reader comes
      // back, rather than leaving the badge stuck on a message now on screen.
      if (document.visibilityState !== "visible") return
      lastMarkedRef.current = message.created
      dispatch(
        markRead({
          conversationId: conversation.id,
          upTo: message.created,
          // Exact: the rendered timestamp is truncated to the second, which is
          // always earlier than the message it came from.
          upToMessage: message.id,
          portal
        })
      )
    },
    [conversation.id, dispatch, portal]
  )

  // The return-to-tab retry lives in MessageHistory, which is the only place
  // that knows whether this conversation is the one on screen — a condition
  // the portal needs, because it mounts every project's conversation at once.

  const handleSend = (body: string, files: File[]) => {
    const clientToken =
      globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
    if (files.length) setUploadProgress(0)
    dispatch(
      sendMessage({
        conversationId: conversation.id,
        body,
        replyTo: replyTo
          ? {
              id: replyTo.id,
              sender_name: replyTo.sender?.full_name ?? "",
              body: replyTo.body,
              is_deleted: replyTo.is_deleted
            }
          : undefined,
        attachments: files,
        clientToken,
        portal,
        onProgress: files.length ? setUploadProgress : undefined
      })
    ).finally(() => setUploadProgress(null))

    handleDraftChange("")
    setReplyTo(null)
  }

  const handleDelete = async (message: Message) => {
    const preview = await api.getDeletePreview(message.id)
    const kept = preview.data?.kept_in_project_files ?? []

    Modal.confirm({
      title: t("messages.deleteConfirm.title", "Delete this message?"),
      content: (
        <div>
          <Typography.Paragraph className={"!mb-2"}>
            {t(
              "messages.deleteConfirm.body",
              "It will be replaced with “This message was deleted.” for everyone in this conversation."
            )}
          </Typography.Paragraph>
          {kept.length > 0 && (
            // The one thing a delete confirmation here has to say out loud:
            // a file already filed into Project Files is a separate item and
            // survives this deletion.
            <Alert
              type={"info"}
              showIcon
              message={t(
                "messages.deleteConfirm.keptInFiles",
                "These attachments stay in Project Files: {{names}}",
                { names: kept.join(", ") }
              )}
            />
          )}
        </div>
      ),
      okText: t("common.delete", "Delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel", "Cancel"),
      onOk: () => dispatch(deleteMessage({ messageId: message.id, portal }))
    })
  }

  const handleJumpToMessage = async (messageId: string) => {
    const known = messages?.some((row) => row.id === messageId)
    if (!known) {
      // The target is older than the page in hand. The server returns it with
      // enough either side to read it in place, which is cheaper and less
      // jarring than paging backwards until it appears.
      const response = await api.getMessageContext(messageId, portal)
      if (response.ok && response.data) {
        dispatch(
          setMessages({
            conversationId: conversation.id,
            messages: response.data.results,
            hasMore: true
          })
        )
      }
    }
    setHighlightedId(messageId)
  }

  if (revokedConversationId === conversation.id) {
    return (
      <Alert
        className={"m-4"}
        type={"warning"}
        showIcon
        message={t("messages.accessRevoked.title", "You no longer have access")}
        description={t(
          "messages.accessRevoked.body",
          "Your access to this conversation was removed. Pick another conversation from the list."
        )}
      />
    )
  }

  return (
    <div className={"flex h-full min-h-0 min-w-0 flex-col bg-white"}>
      <ConversationHeader
        conversation={conversation}
        showBack={showBack}
        isConnected={isConnected}
        onBack={() => onBack?.()}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        onNotificationLevelChange={(level: NotificationLevel) =>
          dispatch(
            updateNotificationLevel({
              conversationId: conversation.id,
              level,
              portal
            })
          )
        }
      />

      <div className={"flex min-h-0 min-w-0 flex-1"}>
        <MessageHistory
          messages={messages ?? []}
          pending={pending}
          participants={conversation.participants}
          currentUserId={currentUserId}
          unreadCount={conversation.unread_count}
          loading={loading}
          error={error}
          hasMore={hasMore}
          highlightedMessageId={highlightedId}
          canSaveToProjectFiles={!portal && Boolean(conversation.project)}
          portal={portal}
          onLoadOlder={() => {
            const oldest = messages?.[0]
            if (oldest) {
              dispatch(
                fetchOlderMessages({
                  conversationId: conversation.id,
                  before: oldest.created,
                  portal
                })
              )
            }
          }}
          onRetryLoad={() =>
            dispatch(fetchMessages({ conversationId: conversation.id, portal }))
          }
          onReply={setReplyTo}
          onEdit={(message, body) =>
            dispatch(editMessage({ messageId: message.id, body, portal }))
          }
          onDelete={handleDelete}
          onJumpToMessage={handleJumpToMessage}
          onSaveToProjectFiles={setSavingAttachment}
          onRetrySend={(row) =>
            dispatch(
              retrySend({
                clientToken: row.client_token,
                conversationId: conversation.id,
                body: row.body,
                portal
              })
            )
          }
          onDiscardSend={(token) => dispatch(discardFailedMessage(token))}
          onReachedMessage={handleReachedMessage}
        />

      </div>

      <MessageComposer
        disabled={!conversation.can_send}
        // The server composes this from the record and the other party, so the
        // composer says where the message is going in the same words the
        // header does.
        placeholder={conversation.composer_placeholder}
        disabledReason={t(
          "messages.composer.archivedRecord",
          "This record is archived. Its chat history stays readable, but nothing new can be sent."
        )}
        participants={conversation.participants}
        currentUserId={currentUserId}
        replyTo={replyTo}
        draft={draft}
        uploadProgress={uploadProgress}
        onDraftChange={handleDraftChange}
        onCancelReply={() => setReplyTo(null)}
        onSend={handleSend}
      />

      {savingAttachment && conversation.project && (
        <SaveToProjectFilesModal
          attachment={savingAttachment}
          projectId={conversation.project}
          isCustomerConversation={conversation.kind === "customer"}
          onClose={() => setSavingAttachment(null)}
        />
      )}
    </div>
  )
}
